import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Plus, Trash2 } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { ImageUpload, inputCls } from "@/admin/fields";
import { Switch } from "@/components/ui/switch";

export default function SocialMediaManager() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/admin/pages/social_media").then((r) => setC(r.data?.content || {})); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/pages/social_media", { content: c });
      const map = {};
      (c.platforms || []).forEach((p) => { map[p.key] = p.enabled ? p.url : ""; });
      await api.put("/admin/settings", { facebook: map.facebook || "", instagram: map.instagram || "", tiktok: map.tiktok || "", youtube: map.youtube || "" });
      toast.success("Social media settings saved");
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (!c) return <Loader2 className="mx-auto mt-20 h-7 w-7 animate-spin text-slate-400" />;
  const set = (k, v) => setC({ ...c, [k]: v });
  const setPlatform = (i, k, v) => { const p = [...c.platforms]; p[i] = { ...p[i], [k]: v }; set("platforms", p); };

  const posts = c.featured_posts || [];
  const setPost = (i, k, v) => { const p = [...posts]; p[i] = { ...p[i], [k]: v }; set("featured_posts", p); };
  const addPost = () => set("featured_posts", [...posts, { platform: "facebook", url: "", image: "", caption: "" }]);
  const removePost = (i) => set("featured_posts", posts.filter((_, idx) => idx !== i));
  const movePost = (i, dir) => {
    const j = i + dir; if (j < 0 || j >= posts.length) return;
    const p = [...posts]; [p[i], p[j]] = [p[j], p[i]]; set("featured_posts", p);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl font-extrabold uppercase text-white">Social Media</h1><p className="text-sm text-slate-400">Manage accounts, featured highlights and the homepage social section.</p></div>
        <button onClick={save} disabled={saving} data-testid="social-save-button" className="inline-flex items-center gap-2 rounded-lg bg-[#E8A317] px-5 py-2 text-sm font-bold uppercase text-[#0A1626]">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</button>
      </div>

      <div className="mt-6 space-y-6">
        <div className="grid gap-4 rounded-xl border border-slate-700 bg-[#151F30] p-5 sm:grid-cols-2">
          <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Section Heading</label><input className={inputCls} value={c.heading || ""} onChange={(e) => set("heading", e.target.value)} data-testid="social-heading" /></div>
          <div className="flex items-center justify-between rounded-lg border border-slate-700 p-3"><div className="text-sm font-bold uppercase text-slate-200">Show on homepage</div><Switch checked={!!c.show_on_home} onCheckedChange={(v) => set("show_on_home", v)} data-testid="social-show-home" /></div>
          <div className="sm:col-span-2"><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Description</label><textarea rows={3} className={inputCls} value={c.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="social-description" /></div>
        </div>

        <div className="rounded-xl border border-slate-700 bg-[#151F30] p-5">
          <h2 className="mb-4 font-heading text-lg font-extrabold uppercase text-white">Platforms</h2>
          <div className="space-y-3">
            {(c.platforms || []).map((p, i) => (
              <div key={p.key} className="grid items-center gap-3 rounded-lg border border-slate-700 bg-[#0F1826] p-4 sm:grid-cols-[120px_1fr_auto]" data-testid={`social-platform-${p.key}`}>
                <div className="text-sm font-bold uppercase text-slate-200">{p.label}</div>
                <input className={inputCls} placeholder={`${p.label} profile URL`} value={p.url || ""} onChange={(e) => setPlatform(i, "url", e.target.value)} data-testid={`social-platform-${p.key}-url`} />
                <div className="flex items-center gap-2"><span className="text-xs text-slate-400">Enabled</span><Switch checked={!!p.enabled} onCheckedChange={(v) => setPlatform(i, "enabled", v)} data-testid={`social-platform-${p.key}-enabled`} /></div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-slate-700 bg-[#151F30] p-5">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-lg font-extrabold uppercase text-white">Featured Posts / Highlights</h2>
            <button onClick={addPost} className="inline-flex items-center gap-1 rounded-md bg-[#1E63D6] px-3 py-1.5 text-xs font-bold text-white" data-testid="social-post-add"><Plus className="h-3.5 w-3.5" /> Add</button>
          </div>
          <div className="space-y-4">
            {posts.map((post, i) => (
              <div key={i} className="rounded-lg border border-slate-700 bg-[#0F1826] p-4" data-testid={`social-post-item-${i}`}>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-bold uppercase text-slate-500">#{i + 1}</span>
                  <div className="flex items-center gap-3">
                    <button onClick={() => movePost(i, -1)} className="text-xs text-slate-400 hover:text-white" data-testid={`social-post-up-${i}`}>↑</button>
                    <button onClick={() => movePost(i, 1)} className="text-xs text-slate-400 hover:text-white" data-testid={`social-post-down-${i}`}>↓</button>
                    <button onClick={() => removePost(i)} className="text-red-400 hover:text-red-300" data-testid={`social-post-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-bold uppercase text-slate-400">Platform</label>
                    <select className={inputCls} value={post.platform || "facebook"} onChange={(e) => setPost(i, "platform", e.target.value)} data-testid={`social-post-${i}-platform`}>
                      <option value="facebook">Facebook</option>
                      <option value="instagram">Instagram</option>
                      <option value="tiktok">TikTok</option>
                      <option value="youtube">YouTube</option>
                    </select>
                  </div>
                  <div><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Post URL</label><input className={inputCls} placeholder="Link to the original post" value={post.url || ""} onChange={(e) => setPost(i, "url", e.target.value)} data-testid={`social-post-${i}-url`} /></div>
                  <div className="sm:col-span-2"><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Caption</label><input className={inputCls} value={post.caption || ""} onChange={(e) => setPost(i, "caption", e.target.value)} data-testid={`social-post-${i}-caption`} /></div>
                  <div className="sm:col-span-2"><label className="mb-1 block text-xs font-bold uppercase text-slate-400">Preview Image</label><ImageUpload value={post.image} onChange={(v) => setPost(i, "image", v)} testid={`social-post-${i}-image`} /></div>
                </div>
              </div>
            ))}
            {posts.length === 0 && <p className="text-sm text-slate-500">No featured posts yet. Click Add to feature a post.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
